'use client'

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { useFacilityBranding } from '@/contexts/SubdomainBrandingContext'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import LanguagePicker from '@/components/ui/LanguagePicker'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { Database } from '@/types/database'
import {
  Settings, User, Bell, Palette, Database as DatabaseIcon,
  LifeBuoy, ArrowLeft, Lock, Mail, Map, CheckCircle2
} from 'lucide-react'
import SEO from '@/components/layout/SEO'

// ─── Design tokens (matches Dashboard / Settings system) ────────────────────
const T = {
  sage: '#4a7c5e', sageDark: '#2d5a3d', sageDeep: '#1e3d2b',
  sageLight: '#e8f2ec', sageMid: '#b8d9c4',
  cream: '#faf7f2', creamDark: '#f0ebe0',
  gold: '#c9903a', navy: '#1a2740', navyMid: '#243452',
  white: '#ffffff', slate: '#4a5568', muted: '#8a94a6',
  error: '#dc2626', errorLight: '#fef2f2', errorBorder: '#fecaca',
  fontDisplay: "'Playfair Display', Georgia, serif",
  fontBody: "'Source Serif 4', Georgia, serif",
}

// ─── Toggle switch ──────────────────────────────────────────────────────────
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      style={{
        width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
        background: checked ? T.sage : '#d8d2c4', border: 'none',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <motion.div
        layout
        transition={{ type: 'spring', stiffness: 500, damping: 32 }}
        style={{
          position: 'absolute', top: 3, left: checked ? 25 : 3,
          width: 20, height: 20, borderRadius: '50%', background: T.white,
          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  )
}

export default function SettingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, userProfile, isLoading: authLoading, updateProfile, refreshUserProfile } = useAuth()
  const supabase = createClientComponentClient()
  const branding = useFacilityBranding()

  // ── White-label context ──────────────────────────────────────────────────
  // Org users (seat-based tenants) never see retail concepts. The generic
  // /subscription page is legacy — every grouping manages plans on its own
  // surface — so its link is disconnected for ALL users.
  const isOrgUser = Boolean((userProfile as any)?.facility_id || branding?.facility_id)
  const orgName = branding?.display_name || ''
  const primary = branding?.primary_color || T.sage
  // Learning Pathway is a university-prep feature
  const showPathway = !isOrgUser || branding?.org_type === 'university'

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveOk, setSaveOk] = useState<boolean | null>(null)

  const [prefs, setPrefs] = useState({
    voiceEnabled: false, largeText: false, highContrast: false, notifications: true,
  })

  // ── Pathway state
  const [isPathwaySaving, setIsPathwaySaving] = useState(false)
  const [pathwayOk, setPathwayOk] = useState<boolean | null>(null)

  const handleUpdatePathway = async (mode: string) => {
    if (!user || isPathwaySaving) return
    setIsPathwaySaving(true); setPathwayOk(null)
    try {
      const { error } = await supabase
        .from('users')
        .update({
          prep_pathway_mode: mode,
          prep_pathway_set_at: new Date().toISOString(),
          prep_pathway_set_by: 'user',
        })
        .eq('id', user.id)
      if (error) throw error
      await refreshUserProfile()
      setPathwayOk(true)
      setTimeout(() => setPathwayOk(null), 3000)
    } catch (err) {
      console.error('Pathway update error:', err)
      setPathwayOk(false)
    } finally {
      setIsPathwaySaving(false)
    }
  }

  // Security state
  const [newPassword, setNewPassword] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordOk, setPasswordOk] = useState<boolean | null>(null)
  const [passwordMessage, setPasswordMessage] = useState('')

  // Email state
  const [newEmail, setNewEmail] = useState('')
  const [isChangingEmail, setIsChangingEmail] = useState(false)
  const [emailOk, setEmailOk] = useState<boolean | null>(null)
  const [emailMessage, setEmailMessage] = useState('')

  useEffect(() => { if (!authLoading && !user) navigate('/auth/login') }, [user, authLoading, navigate])

  useEffect(() => {
    if (userProfile) {
      const p = (userProfile.preferences as Record<string, any>) || {}
      setPrefs({
        voiceEnabled: p.voiceEnabled === true,
        largeText: p.largeText === true,
        highContrast: p.highContrast === true,
        notifications: p.notifications !== false,
      })
      setIsLoading(false)
    }
  }, [userProfile])

  const handlePreferenceChange = (key: string, value: boolean) => {
    setPrefs(prev => ({ ...prev, [key]: value }))
  }

  // ── Save: MERGE into existing preferences. Never replace the whole JSONB —
  // billing.plan, self_learn_active, avatar_url etc. live alongside these keys
  // and were historically wiped by a wholesale save. (AuthContext.updateProfile
  // also merges server-side now; this client-side merge is the second layer.)
  const handleSavePreferences = async () => {
    if (!user) return
    setIsSaving(true); setSaveOk(null)
    try {
      const merged = {
        ...((userProfile?.preferences as Record<string, any>) || {}),
        ...prefs,
      }
      const { error } = await updateProfile({
        preferences: merged as Database['public']['Tables']['users']['Row']['preferences'],
        updated_at: new Date().toISOString(),
      })
      if (error) { setSaveOk(false); console.error('Error updating preferences:', error) }
      else {
        setSaveOk(true)
        localStorage.setItem('ui_prefs', JSON.stringify(prefs))
        window.dispatchEvent(new StorageEvent('storage', { key: 'ui_prefs', newValue: JSON.stringify(prefs) }))
        setTimeout(() => setSaveOk(null), 3000)
      }
    } catch (error) {
      setSaveOk(false); console.error('Error updating preferences:', error)
    } finally { setIsSaving(false) }
  }

  const handleResetMemory = async () => {
    if (!user) return
    const confirmed = window.confirm(
      "Reset your AI's memory notes about you? Saved insights and preferences it has learned will be cleared. Your conversation history is kept."
    )
    if (!confirmed) return
    try {
      const { error } = await supabase.from('user_memories').delete().eq('user_id', user.id)
      if (error) { console.error('Error resetting memory:', error); setSaveOk(false) }
      else { setSaveOk(true); setTimeout(() => setSaveOk(null), 3000) }
    } catch (error) { console.error('Error resetting memory:', error); setSaveOk(false) }
  }

  // Works as change-password AND first-time set-password for Google-only users
  const handleChangePassword = async () => {
    if (newPassword.length < 8) return
    setIsChangingPassword(true); setPasswordOk(null); setPasswordMessage('')
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      setPasswordOk(true)
      setPasswordMessage(t('settings.security.passwordUpdated', 'Password updated successfully!'))
      setNewPassword('')
      setTimeout(() => { setPasswordOk(null); setPasswordMessage('') }, 4000)
    } catch (error: any) {
      setPasswordOk(false)
      setPasswordMessage(error.message || t('settings.security.passwordFailed', 'Failed to update password'))
    } finally { setIsChangingPassword(false) }
  }

  // Sends the branded email_change link (send-email hook) to the new address
  const handleChangeEmail = async () => {
    if (!newEmail || !/^\S+@\S+\.\S+$/.test(newEmail)) {
      setEmailOk(false)
      setEmailMessage(t('settings.security.emailInvalid', 'Enter a valid email address'))
      return
    }
    setIsChangingEmail(true); setEmailOk(null); setEmailMessage('')
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail })
      if (error) throw error
      setEmailOk(true)
      setEmailMessage(t('settings.security.emailSent', 'Confirmation link sent. Check the new inbox to complete the change.'))
      setNewEmail('')
    } catch (error: any) {
      setEmailOk(false)
      setEmailMessage(error.message || t('settings.security.emailFailed', 'Failed to update email'))
    } finally { setIsChangingEmail(false) }
  }

  if (authLoading || isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: T.cream, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) return null

  const cardStyle: React.CSSProperties = {
    background: T.white, borderRadius: '20px', padding: '28px',
    border: `1px solid ${T.creamDark}`, boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  }
  const cardTitleStyle: React.CSSProperties = {
    fontFamily: T.fontDisplay, fontSize: '21px', fontWeight: 700, color: T.navy,
    margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px',
  }
  const rowButtonStyle: React.CSSProperties = {
    width: '100%', textAlign: 'left', padding: '14px 18px', borderRadius: '14px',
    background: T.cream, border: `1px solid ${T.creamDark}`, color: T.navy,
    fontWeight: 600, fontSize: '15px', cursor: 'pointer', fontFamily: T.fontBody,
    transition: 'all 0.15s',
  }
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 16px', borderRadius: '12px',
    border: '1px solid #e2e8f0', marginBottom: 12, fontSize: 15, outline: 'none',
    background: T.white, color: T.navy, boxSizing: 'border-box',
  }

  const statusBanner = (ok: boolean | null, msg?: string) => {
    if (ok === null && !msg) return null
    const success = ok === true
    return (
      <div style={{
        marginTop: 14, padding: '11px 14px', borderRadius: 12, fontWeight: 700,
        fontSize: 13.5, textAlign: 'center',
        background: success ? T.sageLight : T.errorLight,
        color: success ? T.sageDark : T.error,
        border: `1px solid ${success ? T.sageMid : T.errorBorder}`,
      }}>
        {success ? <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} /> : null}
        {msg || (success
          ? t('settings.saved', 'Saved successfully')
          : t('settings.saveFailed', 'Something went wrong. Please try again.'))}
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: T.cream, fontFamily: T.fontBody }}>
      <SEO title="Settings" noindex />

      {/* ══ NAVY HERO BAND ══ */}
      <div style={{ background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 60%, ${isOrgUser ? primary : T.sageDeep} 100%)`, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 300, height: 300, borderRadius: '50%', border: '1px solid rgba(184,217,196,0.12)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button onClick={() => navigate(-1)} aria-label="Back" style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.sageMid }}>
              <ArrowLeft size={18} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(74,124,94,0.3)', border: '1px solid rgba(184,217,196,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Settings size={22} color={T.sageMid} />
              </div>
              <div>
                <h1 style={{ fontFamily: T.fontDisplay, fontSize: 28, fontWeight: 700, color: T.white, margin: 0 }}>{t('settings.title')}</h1>
                <p style={{ fontSize: 14, color: T.sageMid, margin: 0 }}>{t('settings.subtitle')}</p>
              </div>
            </div>
          </div>
        </div>

        <svg viewBox="0 0 1440 48" style={{ display: 'block', width: '100%', height: 48 }} preserveAspectRatio="none">
          <path d="M0,48 C360,0 1080,48 1440,20 L1440,48 Z" fill={T.cream} />
        </svg>
      </div>

      {/* ══ CONTENT ══ */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 20px 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 420px), 1fr))', gap: 20 }}>

          {/* ── Account ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={cardStyle}>
            <h2 style={cardTitleStyle}><User size={20} color="#3b82f6" /> {t('settings.account.title')}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => navigate('/profile')} style={rowButtonStyle}
                onMouseEnter={e => (e.currentTarget.style.borderColor = T.sageMid)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = T.creamDark)}>
                {t('settings.account.profile')}
              </button>
              <button onClick={() => navigate('/privacy')} style={rowButtonStyle}
                onMouseEnter={e => (e.currentTarget.style.borderColor = T.sageMid)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = T.creamDark)}>
                {t('settings.account.privacy')}
              </button>
            </div>
          </motion.div>

          {/* ── Preferences ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} style={cardStyle}>
            <h2 style={cardTitleStyle}><Palette size={20} color={T.sage} /> {t('settings.preferences.title')}</h2>

            <div style={{ background: T.cream, borderRadius: 16, border: `1px solid ${T.creamDark}`, padding: 16, marginBottom: 20 }}>
              <label style={{ fontSize: 10, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 8 }}>
                {t('settings.preferences.language')}
              </label>
              <LanguagePicker />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { key: 'voiceEnabled', label: t('profile.preferences.voiceEnabled'), desc: t('settings.preferences.voiceEnabledDesc', 'Enable voice input for conversations'), icon: null },
                { key: 'largeText', label: t('profile.preferences.largeText'), desc: t('settings.preferences.largeTextDesc', 'Use larger text for better readability'), icon: null },
                { key: 'highContrast', label: t('profile.preferences.highContrast'), desc: t('settings.preferences.highContrastDesc', 'Use high contrast colors for better visibility'), icon: null },
                { key: 'notifications', label: t('settings.preferences.notifications'), desc: t('settings.preferences.notificationsDesc', 'Receive notifications about new features'), icon: <Bell size={14} color={T.muted} /> },
              ].map((pref) => (
                <div key={pref.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingBottom: 14, borderBottom: `1px solid ${T.creamDark}` }}>
                  <div>
                    <p style={{ fontWeight: 700, color: T.navy, margin: 0, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {pref.icon}{pref.label}
                    </p>
                    <p style={{ fontSize: 13, color: T.muted, margin: '2px 0 0' }}>{pref.desc}</p>
                  </div>
                  <Toggle checked={(prefs as any)[pref.key]} onChange={(v) => handlePreferenceChange(pref.key, v)} />
                </div>
              ))}
            </div>

            {statusBanner(saveOk)}

            <button onClick={handleSavePreferences} disabled={isSaving}
              style={{
                width: '100%', marginTop: 20, padding: 14, borderRadius: 14,
                background: primary, color: T.white, border: 'none', fontSize: 16,
                fontWeight: 700, cursor: isSaving ? 'wait' : 'pointer', fontFamily: T.fontBody,
                boxShadow: '0 4px 16px rgba(0,0,0,0.18)', opacity: isSaving ? 0.6 : 1,
              }}>
              {isSaving ? t('settings.saving', 'Saving...') : t('settings.savePreferences', 'Save Preferences')}
            </button>
          </motion.div>
          {/* ── Learning Pathway (university prep) — university contexts only ── */}
          {showPathway && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} style={cardStyle}>
            <h2 style={cardTitleStyle}><Map size={20} color={T.gold} /> Learning Pathway</h2>
            <p style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.6, margin: '0 0 16px' }}>
              How your university experience progresses. Foundation subjects cover critical
              thinking and study skills; bridge subjects prepare you for degree theory.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { id: 'guided', title: 'Guided Pathway (Recommended)', desc: 'Full preparation course shown first — ideal if you\'re new to university-level study.' },
                { id: 'bridge_only', title: 'Bridge Only', desc: 'Skip foundational study skills; focus on subject-specific theory bridges.' },
                { id: 'direct', title: 'Direct Entry', desc: 'Hide all preparation content and go straight to the degree catalogue.' },
              ].map((opt) => {
                const isActive = (userProfile?.prep_pathway_mode || 'guided') === opt.id
                return (
                  <button
                    key={opt.id}
                    onClick={() => handleUpdatePathway(opt.id)}
                    disabled={isPathwaySaving}
                    style={{
                      textAlign: 'left', padding: '14px 16px', borderRadius: 14, cursor: 'pointer',
                      background: isActive ? T.sageLight : T.cream,
                      border: `1.5px solid ${isActive ? T.sage : T.creamDark}`,
                      color: T.navy, fontFamily: T.fontBody, fontSize: 14, fontWeight: isActive ? 700 : 500,
                      opacity: isPathwaySaving ? 0.6 : 1, transition: 'all 0.15s',
                    }}>
                    <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 3 }}>
                      {opt.title} {isActive && <span style={{ fontSize: 10, background: T.sage, color: T.white, padding: '2px 8px', borderRadius: 6, marginLeft: 6 }}>ACTIVE</span>}
                    </div>
                    <div style={{ fontSize: 12.5, color: T.muted, fontWeight: 500 }}>{opt.desc}</div>
                  </button>
                )
              })}
            </div>
            {statusBanner(pathwayOk)}
          </motion.div>
          )}

          {/* ── AI Settings ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={cardStyle}>
            <h2 style={cardTitleStyle}><DatabaseIcon size={20} color="#3b5bdb" /> {t('settings.ai.title')}</h2>
            <div style={{ background: T.cream, borderRadius: 16, border: `1px solid ${T.creamDark}`, padding: 20 }}>
              <p style={{ fontWeight: 700, color: T.navy, margin: '0 0 8px' }}>{t('settings.ai.memory')}</p>
              <p style={{ fontSize: 14, color: T.muted, margin: '0 0 20px', lineHeight: 1.6 }}>
                {t('settings.ai.memoryDesc', "Reset your AI's memory notes about you. Saved insights and preferences it has learned will be cleared. Your conversation history is kept.")}
              </p>
              <button onClick={handleResetMemory}
                style={{ width: '100%', padding: 12, borderRadius: 12, background: 'none', border: '1px solid #fca5a5', color: T.error, fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: T.fontBody, transition: 'all 0.15s' }}>
                {t('settings.ai.resetMemory')}
              </button>
            </div>
          </motion.div>

          {/* ── Security: Email + Password ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} style={cardStyle}>
            <h2 style={cardTitleStyle}><Lock size={20} color={T.navy} /> {t('settings.security.title', 'Security')}</h2>

            {/* Email change — sends the branded email_change confirmation link */}
            <div style={{ background: T.cream, borderRadius: 16, border: `1px solid ${T.creamDark}`, padding: 20, marginBottom: 16 }}>
              <p style={{ fontWeight: 700, color: T.navy, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Mail size={15} color={T.sage} /> {t('settings.security.changeEmail', 'Change Email')}
              </p>
              <p style={{ fontSize: 14, color: T.muted, margin: '0 0 16px', lineHeight: 1.6 }}>
                {t('settings.security.changeEmailDesc', "We'll send a confirmation link to your new address. Your sign-in email changes once you confirm it there.")}
              </p>
              <input
                type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                placeholder={t('settings.security.newEmailPlaceholder', 'New email address')}
                style={inputStyle}
              />
              <button onClick={handleChangeEmail} disabled={!newEmail || isChangingEmail}
                style={{
                  width: '100%', padding: 12, borderRadius: 12, background: T.navy, color: T.white,
                  border: 'none', fontWeight: 700, fontSize: 15, fontFamily: T.fontBody,
                  cursor: !newEmail || isChangingEmail ? 'not-allowed' : 'pointer',
                  opacity: !newEmail || isChangingEmail ? 0.6 : 1,
                }}>
                {isChangingEmail ? t('settings.security.sendingConfirmation', 'Sending confirmation...') : t('settings.security.updateEmail', 'Update Email')}
              </button>
              {(emailOk !== null || emailMessage) && (
                <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600, textAlign: 'center', color: emailOk ? T.sageDark : T.error }}>
                  {emailOk ? <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} /> : null}
                  {emailMessage}
                </div>
              )}
            </div>

            {/* Password — change OR first-time set for Google-only accounts */}
            <div style={{ background: T.cream, borderRadius: 16, border: `1px solid ${T.creamDark}`, padding: 20 }}>
              <p style={{ fontWeight: 700, color: T.navy, margin: '0 0 8px' }}>
                {t('settings.security.changePassword', 'Change Password')}
              </p>
              <p style={{ fontSize: 14, color: T.muted, margin: '0 0 16px', lineHeight: 1.6 }}>
                {t('settings.security.changePasswordDesc', "Set or update your account password. You'll stay logged in.")}
              </p>
              <input
                type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder={t('settings.security.newPasswordPlaceholder', 'New password (min 8 characters)')}
                style={inputStyle}
                onKeyDown={e => { if (e.key === 'Enter' && newPassword.length >= 8) handleChangePassword() }}
              />
              <button onClick={handleChangePassword} disabled={newPassword.length < 8 || isChangingPassword}
                style={{
                  width: '100%', padding: 12, borderRadius: 12, background: T.navy, color: T.white,
                  border: 'none', fontWeight: 700, fontSize: 15, fontFamily: T.fontBody,
                  cursor: newPassword.length < 8 || isChangingPassword ? 'not-allowed' : 'pointer',
                  opacity: newPassword.length < 8 || isChangingPassword ? 0.6 : 1,
                }}>
                {isChangingPassword ? t('settings.security.updating', 'Updating...') : t('settings.security.updatePassword', 'Update Password')}
              </button>
              {(passwordOk !== null || passwordMessage) && (
                <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600, textAlign: 'center', color: passwordOk ? T.sageDark : T.error }}>
                  {passwordOk ? <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} /> : null}
                  {passwordMessage}
                </div>
              )}
            </div>
          </motion.div>

          {/* ── Support ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} style={cardStyle}>
            <h2 style={cardTitleStyle}><LifeBuoy size={20} color={T.gold} /> {t('settings.support.title')}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => navigate('/help')} style={rowButtonStyle}
                onMouseEnter={e => (e.currentTarget.style.borderColor = T.sageMid)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = T.creamDark)}>
                {t('settings.support.help')}
              </button>
              {!isOrgUser && (
                <button onClick={() => { window.location.href = 'mailto:support@stillwatersacademy.com' }} style={rowButtonStyle}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = T.sageMid)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = T.creamDark)}>
                  {t('settings.support.contact')}
                </button>
              )}
            </div>
            <p style={{ fontSize: 12, color: T.muted, margin: '16px 0 0', textAlign: 'center' }}>
              {isOrgUser ? (orgName || 'Organisation') : t('settings.support.entity', 'Still Waters Academy · a service of Still Waters LLC')}
            </p>
          </motion.div>

        </div>
      </div>
    </div>
  )
}